import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorizations } from 'hooks';
import { Grid, Link, Checkbox, FormControlLabel } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { emptyArray, emptyString } from 'constants/defaultValues';
import { get, set } from '@totalsoft/change-tracking-react';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { valueOrDefault } from 'utils/functions';
import SearchOfferContainer from './SearchOfferContainer';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { Autocomplete, Button, CollapseCard, TextField } from '@totalsoft/rocket-ui';
import { CardActionsContainer } from 'components/common/CardStyles';
import { useCallback } from 'react';
import { FinancingOfferLink } from './GeneralDataStyles';

const GeneralDataComponent = ({
  generalDataLens,
  offerTypes,
  offerTypeCodes,
  insuranceTypes,
  isNew,
  onSave,
  saving,
  saveDisabled,
  validation,
  showSearchModal,
  setShowSearchModal,
  financingOfferURL,
  sourceDocumentURL,
  maintainPolicyOption
}) => {
  const { t } = useTranslation();
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const authorizations = useAuthorizations(
    [
      AuthorizationCodes.hasRightToEditGeneralData,
      AuthorizationCodes.hasRightToCopyDataFromFinancingOffer,
      AuthorizationCodes.hasRightToAddOffer
    ],
    documentTypeId,
    documentStateId
  );

  const handleShowSearchModal = useCallback(value => () => setShowSearchModal(value), [setShowSearchModal]);

  return (
    <CollapseCard icon={ListAltIcon} title={t('Offer.Edit.GeneralData.Title')} defaultExpanded toggleOnHeaderClick>
      <>
        <Grid container spacing={3} alignItems={'flex-end'}>
          {sourceDocumentURL && (
            <Grid item xs={12}>
              {t('Offer.Edit.GeneralData.OriginalOffer')}
              <Link href={sourceDocumentURL} underline='hover' color='primary' fontWeight='bold' target='_blank' rel='noopener noreferrer'>
                <>{generalDataLens.sourceDocumentNumber |> get}</>
              </Link>
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id='generalData_offerTypeId'
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              simpleValue={true}
              label={t('Offer.Edit.GeneralData.OfferType')}
              value={generalDataLens.offerTypeId |> get}
              onChange={generalDataLens.offerTypeId |> set}
              options={offerTypes || emptyArray}
              disabled={true}
              error={!isValid(validation.offerTypeId)}
              helperText={getErrors(validation.offerTypeId)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id='generalData_agent'
              fullWidth
              disabled={true}
              value={generalDataLens.userName |> get |> valueOrDefault(emptyString)}
              label={t('Offer.Edit.GeneralData.Agent')}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id='generalData_insuranceTypeId'
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              simpleValue={true}
              disabled={!isNew || (isNew ? !authorizations?.hasRightToAddOffer : !authorizations?.hasRightToEditGeneralData)}
              label={t('Offer.Edit.GeneralData.InsuranceType')}
              value={generalDataLens.insuranceTypeId |> get}
              onChange={generalDataLens.insuranceTypeId |> set}
              options={insuranceTypes || emptyArray}
              error={!isValid(validation.insuranceTypeId)}
              helperText={getErrors(validation.insuranceTypeId)}
            />
          </Grid>
          {maintainPolicyOption && (
            <Grid item xs={12} md={6} lg={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    id={'generalData_currentPolicyMaintained'}
                    checked={generalDataLens.currentPolicyMaintained |> get |> valueOrDefault(false)}
                    disabled={globalReadOnly({
                      controlName: 'CurrentPolicyMaintained',
                      hasAuthorization: authorizations?.hasRightToEditGeneralData
                    })}
                    onChange={generalDataLens.currentPolicyMaintained |> set |> onCheckBoxChange}
                  />
                }
                label={t('Offer.Edit.GeneralData.CurrentPolicyMaintained')}
              />
            </Grid>
          )}
          {financingOfferURL && (generalDataLens.financingDocumentNumber |> get) != null ? (
            <Grid item xs={12} md={6} lg={3}>
              <FinancingOfferLink href={financingOfferURL} underline='hover' color='primary' target='_blank' rel='noopener noreferrer'>
                {t('Offer.Edit.GeneralData.FinancingOfferNumber', {
                  financingOffer: generalDataLens.financingDocumentNumber |> get
                })}
              </FinancingOfferLink>
            </Grid>
          ) : (
            <div></div>
          )}
          {showSearchModal && (
            <SearchOfferContainer
              documentId={generalDataLens.documentId |> get}
              insuranceTypeId={generalDataLens.insuranceTypeId |> get}
              onCloseModal={handleShowSearchModal(false)}
            />
          )}
        </Grid>
        <CardActionsContainer mt={2}>
          <Button
            id='generalData_saveOfferButton'
            color='primary'
            onClick={onSave}
            disabled={saveDisabled || (isNew ? !authorizations?.hasRightToAddOffer : !authorizations?.hasRightToEditGeneralData)}
          >
            {saving ? t('General.Saving') : t('General.Save')}
          </Button>
          {offerTypeCodes &&
            offerTypes &&
            offerTypes.length > 0 &&
            offerTypes.some(o => o.code === offerTypeCodes.Standalone) &&
            (generalDataLens.documentId |> get) &&
            (generalDataLens.offerTypeId |> get) === offerTypes.find(o => o.code === offerTypeCodes.Standalone).id && (
              <Button
                id='generalData_copyDataButton'
                color='primary'
                onClick={handleShowSearchModal(true)}
                disabled={!authorizations?.hasRightToCopyDataFromFinancingOffer}
              >
                {t('Offer.Edit.GeneralData.CopyData')}
              </Button>
            )}
        </CardActionsContainer>
      </>
    </CollapseCard>
  );
};

GeneralDataComponent.propTypes = {
  generalDataLens: PropTypes.object.isRequired,
  offerTypes: PropTypes.array,
  offerTypeCodes: PropTypes.object,
  insuranceTypes: PropTypes.array,
  isNew: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  validation: PropTypes.object,
  showSearchModal: PropTypes.bool.isRequired,
  setShowSearchModal: PropTypes.func.isRequired,
  financingOfferURL: PropTypes.string,
  sourceDocumentURL: PropTypes.string,
  maintainPolicyOption: PropTypes.bool
};

export default GeneralDataComponent;
